import _extends from "@babel/runtime/helpers/extends";
import React, { useMemo } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { ChevronRightBoldIcon } from '@pattern-library/icon';
const propTypes = {
  /**
   * Vertical alignment of the chevron icon.
   * Only applies when `includeChevron` is true.
   */
  alignChevron: PropTypes.oneOf(['top', 'center']),
  /**
   * Extra content to display in the body of the card. Block content is permitted.
   */
  body: PropTypes.node,
  /**
   * Additional attributes for the button element
   **/
  buttonProps: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.object])),
  /**
   * Additional attributes for the card (.asc-card) element
   **/
  cardProps: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.object])),
  /**
   * Scale up slightly on hover
   */
  growOnHover: PropTypes.bool,
  /**
   * Whether to include a chevron icon
   */
  includeChevron: PropTypes.bool,
  /**
   * Function to execute on click
   **/
  onClick: PropTypes.func,
  /**
   * Unique DOM ID for automated tests to locate the wrapper element
   */
  qaId: PropTypes.string,
  /**
   * Apply a box-shadow effect on hover
   */
  shadowOnHover: PropTypes.bool,
  /**
   * Button content.
   * Avoid block and interactive elements and use phrasing elements instead.
   * https://www.w3.org/TR/2012/WD-html5-20120329/the-button-element.html#the-button-element
   **/
  title: PropTypes.node.isRequired,
  /**
   * Button Type; Options: button, submit, reset
   **/
  type: PropTypes.oneOf(['button', 'submit', 'reset'])
};
const defaultProps = {
  type: 'button',
  onClick: () => {},
  buttonProps: {},
  cardProps: {},
  qaId: '',
  alignChevron: 'center',
  includeChevron: false,
  shadowOnHover: false,
  growOnHover: false
};
let idCounter = 0;
function CardButton(props) {
  const {
    type,
    onClick,
    title,
    body,
    alignChevron,
    includeChevron,
    qaId,
    cardProps,
    buttonProps,
    shadowOnHover,
    growOnHover
  } = props;
  const id = useMemo(() => `card-btn-${idCounter++}`, []);
  let cardClasses = classNames('asc-card', 'asc-card--border', 'asc-card-btn', includeChevron && `asc-card-btn--chevron-${alignChevron}`, {
    'asc-card-btn--shadow': shadowOnHover,
    'asc-card-btn--grow': growOnHover
  }, cardProps?.className);
  let buttonClasses = classNames('asc-card-btn-title', buttonProps?.className);
  const bodyId = body ? `${id}-body` : null;
  const ariaDescribedByIds = [buttonProps['aria-describedby'], bodyId].filter(Boolean).join(' ');
  return /*#__PURE__*/React.createElement("div", _extends({}, cardProps, {
    className: cardClasses,
    "data-qa": qaId
  }), /*#__PURE__*/React.createElement("div", {
    className: "asc-card-btn-layout"
  }, /*#__PURE__*/React.createElement("button", _extends({}, buttonProps, {
    type: type,
    className: buttonClasses,
    onClick: onClick,
    "aria-describedby": ariaDescribedByIds || undefined
  }), title), includeChevron ? /*#__PURE__*/React.createElement(ChevronRightBoldIcon, {
    className: "asc-card-btn-icon",
    color: "brand-link"
  }) : null, body ? /*#__PURE__*/React.createElement("div", {
    className: "asc-card-btn-body",
    id: bodyId
  }, body) : null));
}
CardButton.propTypes = propTypes;
CardButton.defaultProps = defaultProps;
export default CardButton;